import React, { useState, useEffect } from 'react';
import { Grid, Typography, Modal, CircularProgress, FormHelperText, Autocomplete } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import showToast from 'components/Toast/Toast';
import { getCommunityById, editWeekPost } from 'utils/requests/communities';
import { requestSinglePostDify } from 'utils/requests/dify';
import * as S from './style';
import { useFormik } from 'formik';
import * as Yup from "yup";

const formatOptions = [
  { value: 'video', label: 'Vídeo' },
  { value: 'reel', label: 'Reel' },
  { value: 'story', label: 'Story' },
  { value: 'post', label: 'Post' },
  { value: 'carousel', label: 'Carrossel' }
];

const objectiveOptions = [
  { value: 'engage', label: 'Engajar meu público' },
  { value: 'promote', label: 'Divulgar um produto ou curso' },
  { value: 'inspire', label: 'Inspirar com algo pessoal' },
  { value: 'response', label: 'Gerar comentário ou resposta' },
];

const NewPostForm = ({ selectedContent, onClose, onSuccess, communityId, openModal }) => {
  const [isReprogramming, setIsReprogramming] = useState(false);
  const [validateFields, setValidateFields] = useState(false);

  useEffect(() => {
    if (openModal) {
      formik.resetForm();
      setValidateFields(false);
    }
  }, [openModal]);

  const formik = useFormik({
    initialValues: {
      whatDidntLike: '',
      howWouldLike: '',
      format: selectedContent?.format || '',
      objective: selectedContent?.objective || ''
    },

    validateOnChange: validateFields,
    validateOnBlur: false,

    validationSchema: Yup.object({
      whatDidntLike: Yup.string().required('O campo é obrigatório'),
      howWouldLike: Yup.string().required('O campo é obrigatório'),
      format: Yup.string().required('O formato é obrigatório'),
      objective: Yup.string().required('O objetivo é obrigatório')
    }),

    onSubmit: async (values) => {
      setIsReprogramming(true);
      try {
        const communityData = await getCommunityById(communityId);
        
        const singlePostPayload = {
          name: communityData.plan_details.name,
          tagline: communityData.plan_details.tagline,
          purpose: communityData.plan_details.purpose,
          platform: communityData.plan_details.platform,
          subscription: communityData.plan_details.subscription,
          tone: communityData.plan_details.tone,
          customTone: communityData.plan_details.customTone,
          currentTitle: selectedContent.title,
          currentDescription: selectedContent.description,
          weekNumber: selectedContent.week.title.match(/Semana (\d+)/)?.[1] || '1',
          format: formik.values.format,
          objective: formik.values.objective,
          feedback: formik.values.whatDidntLike,
          howWouldLike: formik.values.howWouldLike,
          currentTemplates: selectedContent.templates
        };
        const response = await requestSinglePostDify(singlePostPayload);
        if (response && response.answer) {
          try {
            const newPost = typeof response.answer === 'string' ? JSON.parse(response.answer) : response.answer;
            
            const updatedScriptData = {
              title: newPost.title,
              description: newPost.description,
              format: newPost.format || formik.values.format,
              objective: newPost.objective || formik.values.objective,
              tone: newPost.tone || selectedContent.tone,
              completed: false,
              feedback: `Feedback: ${formik.values.whatDidntLike} | Como gostaria: ${formik.values.howWouldLike}`,
              templates: newPost.templates || selectedContent.templates
            };

            if (!newPost.title || !newPost.description) {
              throw new Error('Resposta inválida: título ou descrição não encontrados');
            }

            await editWeekPost(selectedContent.id, {
              post_title: newPost.title,
              script_data: JSON.stringify(updatedScriptData)
            });

            showToast("Post reprogramado com sucesso!", "success");
            onSuccess(newPost, selectedContent);
            onClose();
          } catch (parseError) {
            console.error("Erro ao fazer parse da resposta:", parseError);
            showToast("Erro ao processar resposta do servidor", "error");
          }
        } else {
          throw new Error('Resposta inválida do servidor');
        }
      } catch (error) {
        console.error("Erro ao reprogramar conteúdo:", error);
        showToast("Erro ao reprogramar conteúdo", "error");
      } finally {
        setIsReprogramming(false);
      }
    }
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    formik.setFieldValue(name, value);
    setValidateFields(true);
  };

  return (
    <Modal
      open={openModal}
      onClose={onClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <S.ModalContainer>
        <S.CloseButton
          aria-label="close"
          onClick={onClose}
        >
          <CloseIcon />
        </S.CloseButton>
        <S.ModalTitle>
          <S.ModalTitleText variant="h5">
            Gerar Novo Post
          </S.ModalTitleText>
        </S.ModalTitle>
        <S.ModalContent>
          <S.FormSection>
            <S.SectionTitle variant="h6">
              {selectedContent?.title}
            </S.SectionTitle>
            <S.SectionTitle variant="h6">
              {selectedContent?.description}
            </S.SectionTitle>
            <Typography variant="body2" sx={{ color: "#8F8F8F", mb: 3, fontStyle: "italic" }}>
              Preencha o formulário abaixo para que possamos gerar um novo post que atenda melhor às suas expectativas.
            </Typography>
          </S.FormSection>
          <S.FormSection>
            <S.SectionTitle variant="h6">
              O que você não gostou deste post?
            </S.SectionTitle>
            <S.InputGridBuildScript item xs={12}>
              <S.InputBuildScript
                fullWidth
                multiline
                value={formik.values.whatDidntLike}
                onChange={(e) => formik.setFieldValue('whatDidntLike', e.target.value)}
                placeholder="Descreva o que não funcionou bem"
              />
            </S.InputGridBuildScript>
            {formik.touched.whatDidntLike && formik.errors.whatDidntLike && (
              <FormHelperText error sx={{ color: '#f44336', mt: 1 }}>
                {formik.errors.whatDidntLike}
              </FormHelperText>
            )}
          </S.FormSection>
          <S.FormSection>
            <S.SectionTitle variant="h6">
              Como você gostaria que o novo post fosse?
            </S.SectionTitle>
            <S.InputGridBuildScript item xs={12}>
              <S.InputBuildScript
                fullWidth
                multiline
                value={formik.values.howWouldLike}
                onChange={(e) => formik.setFieldValue('howWouldLike', e.target.value)}
                placeholder="Descreva como gostaria que fosse"
              />
            </S.InputGridBuildScript>
            {formik.touched.howWouldLike && formik.errors.howWouldLike && (
              <FormHelperText error sx={{ color: '#f44336', mt: 1 }}>
                {formik.errors.howWouldLike}
              </FormHelperText>
            )}
          </S.FormSection>
          <S.FormSection>
            <Grid container flexDirection="row" justifyContent="space-between">
              <Grid item xs={5.85}>
                <S.SectionTitle variant="h6">
                  Formato
                </S.SectionTitle>
                <S.InputGridBuildScript item xs={12}>
                  <Autocomplete
                    limitTags={10}
                    disablePortal
                    name="format"
                    value={formatOptions.find(option => option.value === formik.values.format) || null}
                    onChange={(e, value) => {
                      handleChange({
                        target: {
                          name: 'format',
                          value: value ? value.value : ''
                        }
                      });
                    }}
                    options={formatOptions}
                    sx={{ width: "100%" }}
                    renderInput={(params) => <S.Input {...params} placeholder='Escolha o formato' />}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option?.label === value?.label}
                  />
                </S.InputGridBuildScript>
                {formik.touched.format && formik.errors.format && (
                  <FormHelperText error sx={{ color: '#f44336', mt: 1 }}>
                    {formik.errors.format}
                  </FormHelperText>
                )}
              </Grid>
              <Grid item xs={5.85}>
                <S.SectionTitle variant="h6">
                  Objetivo
                </S.SectionTitle>
                <S.InputGridBuildScript item xs={12}>
                  <Autocomplete
                    limitTags={10}
                    disablePortal
                    name="objective"
                    value={objectiveOptions.find(option => option.value === formik.values.objective) || null}
                    onChange={(e, value) => {
                      handleChange({
                        target: {
                          name: 'objective',
                          value: value ? value.value : ''
                        }
                      });
                    }}
                    options={objectiveOptions}
                    sx={{ width: "100%" }}
                    renderInput={(params) => <S.Input {...params} placeholder='Escolha o objetivo' />}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option?.label === value?.label}
                  />
                </S.InputGridBuildScript>
                {formik.touched.objective && formik.errors.objective && (
                  <FormHelperText error sx={{ color: '#f44336', mt: 1 }}>
                    {formik.errors.objective}
                  </FormHelperText>
                )}
              </Grid>
            </Grid>
          </S.FormSection>
        </S.ModalContent>
        <S.ButtonContainer>
          <S.SubmitButton
            variant="contained"
            onClick={formik.handleSubmit}
            disabled={isReprogramming}
          >
            {isReprogramming && <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />}
            {isReprogramming ? 'Gerando novo post...' : 'Gerar Novo Post'}
          </S.SubmitButton>
        </S.ButtonContainer>
      </S.ModalContainer>
    </Modal>
  );
};

export default NewPostForm; 